import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckBadgeIcon, CheckIcon, ListBulletIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { hasAnyRole } from "../../../utils/auth";
import { ROLES } from "../../../auth/roles";
import { Button, ConfirmModal, EmptyState, InfoBox, Loading, RowAction, Select, Table, Tag } from "../../ui";
import { errorMessageOf, newIdempotencyKey, problemOf, scholarshipsApi } from "../../../api/scholarships";
import { SCHOLARSHIPS_KEY, kindOf, needsSponsor, staffScholarshipKey } from "../model";
import { REVIEW_KEY, awardListKey, formatDateTime, formatScore, fullName, outcomeOf, outcomesFor, topOf } from "./model";
import { amountsOf, loanSummary } from "../funding";
import ApprovedFundingDialog from "./ApprovedFundingDialog";
import { loanStatusOf, loansOn } from "../loans/model";
import { TransmissionDialog, nomineesGo, transmissionKey } from "../cooperation/Transmission";

const capitalise = (text) => text.charAt(0).toUpperCase() + text.slice(1);
// "the top applicant is", "the top 3 applicants are", or every ranked applicant without a quota

// Ranks follow the order of the awarded, then the reserve list, by score
// What is wrong with one entry, from `entries-incomplete`
const ENTRY_PROBLEMS = {
  "not-a-candidate": "is not an application of this scholarship",
  "duplicate-application": "appears twice",
  "rank-required": "needs a reserve rank",
  "duplicate-rank": "has the same rank as another student",
  "rank-not-allowed": "cannot have a rank with this outcome",
  missing: "is missing from the list; reload the award list",
  "outcome-already-decided": "was decided earlier, in the abroad scholarship records, and cannot change",
};

// Outcomes decided earlier, in the abroad scholarship records, stay as they are and are sent back unchanged
const decidedBefore = (e) => e.reasonCode === "DECIDED_EARLIER";

// What a save would change on an entry: its outcome, its reserve rank and its approved loan
const savedPartOf = ({ outcome, rank, approvedFunding }) => JSON.stringify([outcome, rank ?? null, approvedFunding ?? null]);
const differs = (edited, saved) => {
  const before = new Map(saved.map((e) => [e.applicationId, savedPartOf(e)]));
  return edited.some((e) => before.get(e.applicationId) !== savedPartOf(e));
};

const withRanks = (entries) => {
  // Only reserves are ranked (the backend refuses a rank on any other outcome). Outcomes decided
  // earlier (DECIDED_EARLIER) keep their rank; the others are numbered after them
  const fixed = entries.filter(decidedBefore);
  const ranked = { RESERVE: 0 };
  fixed.forEach((e) => {
    if (e.outcome in ranked) ranked[e.outcome] = Math.max(ranked[e.outcome], e.rank ?? 0);
  });
  return [...entries]
    .sort((a, b) => (b.meanScore ?? -1) - (a.meanScore ?? -1))
    .map((e) => {
      if (fixed.includes(e)) return e;
      return e.outcome in ranked ? { ...e, rank: (ranked[e.outcome] += 1) } : { ...e, rank: null };
    });
};

/**
 * Journey step 10: who gets the scholarship or loan. Created from the ranking once applications
 * close (the top places awarded, or nominated for a sponsored scholarship) and adjusted. A government
 * loan's award list or a sponsored scholarship's nomination list is made by the HEC Admin or the DG. The
 * HEC Admin's goes to the DG to sign off; the DG's goes straight to the Minister or the sponsor.
 */
export default function AwardListPanel({ scholarship }) {
  const queryClient = useQueryClient();
  const isAdmin = hasAnyRole(ROLES.ADMIN);
  const isDg = hasAnyRole(ROLES.DG);
  const [entries, setEntries] = useState(null); // edited outcomes, before saving
  const [selected, setSelected] = useState([]); // application IDs ticked for one outcome at once
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [notReviewed, setNotReviewed] = useState([]);
  const [unfunded, setUnfunded] = useState([]); // names of awarded students without approved amounts
  const [entryProblems, setEntryProblems] = useState([]); // one sentence per refused entry
  const [fundingFor, setFundingFor] = useState(null); // the entry whose loan is being set
  const [createKey] = useState(newIdempotencyKey);
  const sponsored = needsSponsor(scholarship.type);
  const listName = sponsored ? "nomination list" : "award list";
  const outcomes = outcomesFor(sponsored);
  // The DG makes the list as the HEC Admin does, but only the DG sends it on
  const canDecide = isAdmin || isDg;
  const sendTo = !isDg ? "the DG" : sponsored ? "the partner" : "the Minister";
  const labelOf = (value) => (outcomes.find((o) => o.value === value) ?? outcomeOf(value)).label;

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: awardListKey(scholarship.id),
    retry: (count, err) => problemOf(err)?.status !== 404 && count < 2,
    queryFn: () => scholarshipsApi.get(`admin/scholarships/${scholarship.id}/award-list`),
  });
  const list = data?.data;
  const missing = isError && problemOf(error)?.status === 404;
  // A government loan's award list goes on to the DG and the Minister instead of being published; one carried
  // over with every outcome decided earlier never went there
  const decidedList = list?.entries.length > 0 && list.entries.every(decidedBefore);
  const loans = loansOn(scholarship) && !decidedList;
  // The DG's own nomination list goes straight to the sponsor, so the DG first previews what it receives
  const previewFirst = isDg && sponsored && !loans;

  useEffect(() => {
    setEntries(null);
    setSelected([]);
  }, [list?.version]);

  const refresh = (result) => {
    queryClient.setQueryData(awardListKey(scholarship.id), result);
    queryClient.invalidateQueries({ queryKey: REVIEW_KEY });
    queryClient.invalidateQueries({ queryKey: staffScholarshipKey(scholarship.id) });
    queryClient.invalidateQueries({ queryKey: [...SCHOLARSHIPS_KEY, "staff", "list"] });
    queryClient.invalidateQueries({ queryKey: transmissionKey(scholarship.id) });
  };

  const create = async () => {
    setBusy(true);
    try {
      refresh(await scholarshipsApi.post(`admin/scholarships/${scholarship.id}/award-list`, null, { idempotencyKey: createKey }));
      toast.success(`${capitalise(listName)} created from the ranking`);
    } catch (err) {
      console.error(`Failed to create the award list of scholarship ${scholarship.id}:`, err);
      toast.error(errorMessageOf(err, `Failed to create the ${listName}. Please try again.`));
    } finally {
      setBusy(false);
    }
  };

  const shown = entries ?? list?.entries ?? [];
  // Only a real difference counts: ticking students who already have the outcome changes nothing
  const changed = entries !== null && differs(entries, list?.entries ?? []);
  // An awarded student's loan starts from what they asked for; other outcomes have no loan
  const setOutcomes = (applicationIds, outcome) =>
    setEntries(
      withRanks(
        shown.map((e) =>
          applicationIds.includes(e.applicationId) ? { ...e, outcome, approvedFunding: loans && outcome === "AWARDED" ? e.approvedFunding ?? amountsOf(e.requestedFunding) : null } : e
        )
      )
    );
  const setOutcome = (applicationId, outcome) => setOutcomes([applicationId], outcome);
  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  // Ticked students all get the outcome; the ticks stay so another outcome can follow. Students not
  // ticked keep theirs, so say when the ticked ones had it already
  const setSelectedOutcome = (outcome) => {
    const already = shown.filter((e) => selected.includes(e.applicationId) && e.outcome === outcome).length;
    if (already === selected.length) toast.info(`${selected.length === 1 ? "The selected student is" : `The ${selected.length} selected are`} already ${labelOf(outcome)}.`);
    setOutcomes(selected, outcome);
  };
  const setFunding = (applicationId, approvedFunding) => {
    setEntries(shown.map((e) => (e.applicationId === applicationId ? { ...e, approvedFunding } : e)));
    setFundingFor(null);
  };

  // `entries[i]…` points at the list as sent, `applications[<id>]` at an application
  const describeEntryProblems = (problem, sent) =>
    (problem?.errors ?? []).map((e) => {
      const index = /^entries\[(\d+)\]/.exec(e.field ?? "")?.[1];
      const id = /^applications\[(.+?)\]/.exec(e.field ?? "")?.[1];
      const entry = index != null ? sent[Number(index)] : shown.find((x) => x.applicationId === id);
      return `${entry ? fullName(entry.applicant) : "An application"} ${ENTRY_PROBLEMS[e.code] ?? "is not valid"}.`;
    });

  const save = async () => {
    setBusy(true);
    setEntryProblems([]);
    try {
      const body = { entries: shown.map(({ applicationId, outcome, rank, reasonCode, approvedFunding }) => ({ applicationId, outcome, rank, reasonCode, ...(loans && { approvedFunding }) })) };
      refresh(await scholarshipsApi.put(`admin/award-lists/${list.id}/entries`, body, { ifMatch: data.etag ?? `"${list.version}"` }));
      toast.success(`${capitalise(listName)} saved`);
    } catch (err) {
      console.error(`Failed to save award list ${list.id}:`, err);
      if (problemOf(err)?.code === "entries-incomplete") setEntryProblems(describeEntryProblems(problemOf(err), shown));
      toast.error(errorMessageOf(err, `Failed to save the ${listName}. Please try again.`));
      if (problemOf(err)?.code === "stale-version") refetch();
    } finally {
      setBusy(false);
    }
  };

  // `note`: the DG's note on the transmission letter when they send their own list to the sponsor
  const finalise = async (note) => {
    setBusy(true);
    setNotReviewed([]);
    setUnfunded([]);
    setEntryProblems([]);
    try {
      // Always JSON: an empty body would go as a form, which the API refuses
      const body = note ? { note } : {};
      refresh(await scholarshipsApi.post(`admin/award-lists/${list.id}/finalise`, body, { ifMatch: data.etag ?? `"${list.version}"`, idempotencyKey: newIdempotencyKey() }));
      toast.success(`${capitalise(listName)} sent to ${sendTo}`);
      setConfirming(false);
    } catch (err) {
      const problem = problemOf(err);
      setConfirming(false);
      // Finalised meanwhile (another tab, a retried click): already done, so show the list as it is
      if (problem?.code === "award-list-finalised") {
        console.warn(`Award list ${list.id} was already finalised; reloading it`);
        toast.info(errorMessageOf(err));
        refetch();
        return;
      }
      console.error(`Failed to finalise award list ${list.id}:`, err);
      if (problem?.code === "applications-not-reviewed") {
        const ids = problem.errors.map((e) => /\[(.+)\]/.exec(e.field ?? "")?.[1]);
        setNotReviewed(shown.filter((e) => ids.includes(e.applicationId)).map((e) => fullName(e.applicant)));
      }
      if (problem?.code === "entries-incomplete") setEntryProblems(describeEntryProblems(problem, shown));
      if (problem?.code === "approved-funding-required") {
        const ids = problem.errors.map((e) => /\[(.+?)\]/.exec(e.field ?? "")?.[1]);
        setUnfunded(shown.filter((e) => ids.includes(e.applicationId)).map((e) => fullName(e.applicant)));
      }
      toast.error(errorMessageOf(err, `Failed to send the ${listName}. Please try again.`));
    } finally {
      setBusy(false);
    }
  };

  if (isPending) return <Loading label={`Loading the ${listName}…`} />;

  if (missing) {
    const closed = scholarship.state === "CLOSED";
    return (
      <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed border-slate-200 p-10 text-center">
        <ListBulletIcon className="h-8 w-8 text-slate-400" />
        <p className="max-w-md text-sm text-slate-600">
          {closed && !canDecide
            ? `HEC has not made the ${listName} yet.`
            : closed
            ? `Start the ${listName} from the ranking: ${
                topOf(scholarship.awardQuota)
                  ? `${topOf(scholarship.awardQuota)} ${sponsored ? "nominated" : "awarded"}; everyone else, including applications not fully marked yet, is not`
                  : `nobody is ${sponsored ? "nominated" : "awarded"} yet, as there is no quota`
              }. You can adjust it before sending it to ${sendTo}.`
            : `The ${listName} can be made once applications close.`}
        </p>
        {closed && canDecide && (
          <Button icon={<ListBulletIcon className="h-4 w-4" />} onClick={create} loading={busy}>
            Create {listName}
          </Button>
        )}
      </div>
    );
  }
  if (isError) return <EmptyState title={`Could not load the ${listName}`} onRefresh={refetch} />;

  const finalised = list.state === "FINALISED";
  const editable = canDecide && !finalised;
  const counts = shown.reduce((acc, e) => ({ ...acc, [e.outcome]: (acc[e.outcome] ?? 0) + 1 }), {});
  // Outcomes decided in the abroad records cannot change, so they cannot be ticked
  const changeable = editable ? shown.filter((e) => !decidedBefore(e)) : [];
  const allSelected = changeable.length > 0 && changeable.every((e) => selected.includes(e.applicationId));

  const columns = [
    ...(changeable.length
      ? [
          {
            key: "select",
            label: (
              <input
                type="checkbox"
                aria-label="Select every applicant"
                checked={allSelected}
                onChange={() => setSelected(allSelected ? [] : changeable.map((e) => e.applicationId))}
                className="h-4 w-4 accent-brand"
                disabled={busy}
              />
            ),
            exportable: false,
            sortable: false,
            render: (e) =>
              decidedBefore(e) ? null : (
                <input
                  type="checkbox"
                  aria-label={`Select ${fullName(e.applicant)}`}
                  checked={selected.includes(e.applicationId)}
                  onClick={(ev) => ev.stopPropagation()}
                  onChange={() => toggle(e.applicationId)}
                  className="h-4 w-4 accent-brand"
                  disabled={busy}
                />
              ),
          },
        ]
      : []),
    { key: "rank", label: "Reserve rank", accessor: (e) => e.rank ?? "—", sortValue: (e) => e.rank ?? 999 },
    { key: "applicant", label: "Applicant", accessor: (e) => fullName(e.applicant), render: (e) => <span className="font-medium text-slate-800">{fullName(e.applicant)}</span> },
    { key: "programme", label: "Programme", accessor: (e) => e.programme.name },
    { key: "meanScore", label: "Score", accessor: (e) => formatScore(e.meanScore), sortValue: (e) => e.meanScore },
    {
      key: "outcome",
      label: "Outcome",
      accessor: (e) => labelOf(e.outcome),
      render: (e) =>
        editable && !decidedBefore(e) ? (
          <Select aria-label={`Outcome for ${fullName(e.applicant)}`} value={e.outcome} options={outcomes} onChange={(ev) => setOutcome(e.applicationId, ev.target.value)} className="w-40" disabled={busy} />
        ) : (
          <span className="flex flex-col gap-0.5">
            <Tag color={outcomeOf(e.outcome).color}>{labelOf(e.outcome)}</Tag>
            {decidedBefore(e) && <span className="text-xs text-slate-500">Decided in the abroad records</span>}
          </span>
        ),
    },
    ...(finalised
      ? [
          {
            key: "loanStatus",
            label: "Approval",
            accessor: (e) => (e.loanStatus ? loanStatusOf(e.loanStatus, !sponsored).label : "—"),
            render: (e) => (e.loanStatus ? <Tag color={loanStatusOf(e.loanStatus, !sponsored).color}>{loanStatusOf(e.loanStatus, !sponsored).label}</Tag> : "—"),
          },
        ]
      : []),
    ...(loans
      ? [
          {
            key: "loan",
            label: "Approved loan",
            accessor: (e) => (e.outcome === "AWARDED" ? loanSummary(e.approvedFunding) : "—"),
            render: (e) =>
              e.outcome === "AWARDED" ? (
                <span className="flex items-center gap-1">
                  <span className={e.approvedFunding?.tuitionFee == null ? "text-amber-700" : "text-slate-800"}>{loanSummary(e.approvedFunding)}</span>
                  {editable && <RowAction Icon={PencilSquareIcon} label="Set loan amounts" onClick={() => setFundingFor(e)} disabled={busy} />}
                </span>
              ) : (
                "—"
              ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      {finalised ? (
        <InfoBox>
          Sent {formatDateTime(list.finalisedAt)}
          {list.finalisedBy?.name && ` by ${list.finalisedBy.name}`}
          {!loans && (list.signedOffAt ? " · signed off and sent to the partner" : " · waiting for the DG's sign-off")}
          {" · "}
          <Link to={`${kindOf(scholarship.type).approvalsPath}/${scholarship.id}`} className="font-medium text-brand hover:underline">
            Approvals
          </Link>
        </InfoBox>
      ) : list.returnReason ? (
        <InfoBox>
          Returned by {list.returnedBy?.name ?? "the DG"} {formatDateTime(list.returnedAt)}: “{list.returnReason}”. Change the list if needed, then send it again.
        </InfoBox>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {outcomes.map((o) => (
          <Tag key={o.value} color={o.color}>
            {o.label}: {counts[o.value] ?? 0}
          </Tag>
        ))}
        {changed && <span className="text-sm text-amber-700">Unsaved changes</span>}
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 p-3">
          <span className="text-sm font-medium text-slate-700">{selected.length} selected:</span>
          {outcomes.map((o) => (
            <Button key={o.value} size="sm" variant="outline" onClick={() => setSelectedOutcome(o.value)} disabled={busy}>
              {o.label}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setSelected([])} disabled={busy}>
            Clear selection
          </Button>
        </div>
      )}
      <Table columns={columns} data={shown} searchable={false} exportName={`award-list-${scholarship.name}`} emptyTitle="No reviewed applications" />
      {unfunded.length > 0 && <p className="text-sm text-red-600">Set the approved loan for: {unfunded.join(", ")}.</p>}
      {entryProblems.length > 0 && (
        <ul className="list-disc pl-5 text-sm text-red-600">
          {entryProblems.map((message, i) => (
            <li key={i}>{message}</li>
          ))}
        </ul>
      )}
      {notReviewed.length > 0 && (
        <p className="text-sm text-red-600">Still being marked: {notReviewed.join(", ")}. Finalise once every evaluator has submitted.</p>
      )}
      {editable && (
        <div className="flex flex-col-reverse justify-end gap-3 sm:flex-row">
          <Button variant="ghost" onClick={() => setEntries(null)} disabled={busy || !changed}>
            Discard changes
          </Button>
          <Button variant="outline" icon={<CheckIcon className="h-4 w-4" />} onClick={save} loading={busy && changed} disabled={!changed}>
            Save {listName}
          </Button>
          <Button icon={<CheckBadgeIcon className="h-4 w-4" />} onClick={() => setConfirming(true)} disabled={busy || changed}>
            {previewFirst ? "Preview and send" : `Send to ${sendTo}`}
          </Button>
        </div>
      )}
      <ApprovedFundingDialog key={fundingFor?.applicationId} entry={fundingFor} onApply={(funding) => setFunding(fundingFor.applicationId, funding)} onClose={() => setFundingFor(null)} />
      {previewFirst ? (
        <TransmissionDialog
          scholarship={scholarship}
          open={confirming}
          onClose={() => setConfirming(false)}
          onSend={finalise}
          busy={busy}
          description={`${nomineesGo(counts.AWARDED)} to the partner with this letter and their documents; ${counts.RESERVE ?? 0} stay on the reserve list and ${counts.NOT_AWARDED ?? 0} not nominated are told now. This cannot be undone.`}
        />
      ) : (
        <ConfirmModal
          open={confirming}
          onClose={() => setConfirming(false)}
          onConfirm={() => finalise()}
          loading={busy}
          title={`Send the ${listName} to ${sendTo}?`}
          description={
            loans
              ? `${counts.AWARDED ?? 0} awarded go to ${sendTo} for approval with their approved loans; ${counts.RESERVE ?? 0} stay on the reserve list and ${counts.NOT_AWARDED ?? 0} not awarded are told now. This cannot be undone.`
              : isDg
                ? `${counts.AWARDED ?? 0} nominated go to the partner, who grants or refuses them; ${counts.RESERVE ?? 0} stay on the reserve list and ${counts.NOT_AWARDED ?? 0} not nominated are told now. This cannot be undone.`
                : `${counts.AWARDED ?? 0} nominated go to the DG to sign off, then to the partner; ${counts.RESERVE ?? 0} stay on the reserve list and ${counts.NOT_AWARDED ?? 0} not nominated are told once the DG signs off.`
          }
          confirmLabel={`Send to ${sendTo}`}
        />
      )}
    </div>
  );
}
